// Pages a role can't use (ERP-162). The API refuses them with a 403; before this the page waited for
// data that never came, or showed zeros, so the user could not tell "empty" from "not allowed".
import { onForbidden, t, useAuth } from "@keel/core";
import { NoAccess, permissionPhrase } from "@keel/ui";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/** The page, or "No access" when the role lacks the permission the page needs. */
export function RouteGuard({ perm, children }: { perm: string | null; children: ReactNode }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  if (perm && !can(perm))
    return <NoAccess permission={perm} onRetry={() => void qc.invalidateQueries({ queryKey: ["me"] })} />;
  return (
    <>
      <RefusedBanner />
      {children}
    </>
  );
}

/**
 * The page is allowed, but something on it was refused: say what, so a missing figure or a list
 * that never fills is explained. Cleared when the user moves to another page.
 */
function RefusedBanner() {
  const { pathname } = useLocation();
  const { can } = useAuth();
  const [refused, setRefused] = useState<{ at: string; perms: string[] }>({ at: pathname, perms: [] });
  useEffect(
    () =>
      onForbidden(({ permission }) => {
        if (!permission) return;
        setRefused((r) => {
          const perms = r.at === pathname ? r.perms : [];
          return perms.includes(permission) ? r : { at: pathname, perms: [...perms, permission] };
        });
      }),
    [pathname],
  );
  // A permission granted since (the session re-reads them after a refusal) no longer counts.
  const missing = refused.at === pathname ? refused.perms.filter((p) => !can(p)) : [];
  if (missing.length === 0) return null;
  return (
    <div className="banner banner-warn" role="status">
      {t("Part of this page is hidden: your role doesn't have {perms}.", {
        perms: missing.map((p) => `${permissionPhrase(p)} (${p})`).join(", "),
      })}
    </div>
  );
}
